import { Landmark } from "lucide-react";
import { useTranslate } from "ra-core";
import { Link } from "react-router";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import {
  AgingChart,
  ConcentrationChart,
  ExpensesByCategoryChart,
} from "./BreakdownCharts";
import { FinanceAlertsPanel } from "./FinanceAlertsPanel";
import { KpiCards } from "./KpiCards";
import {
  BilledReceivedChart,
  ForecastChart,
  MrrChart,
  ResultChart,
  RevenueMixChart,
} from "./TrendCharts";
import { useFinanceData } from "./useFinanceData";

const OverviewSkeleton = () => (
  <div className="flex flex-col gap-3" aria-busy>
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton key={index} className="h-20" />
      ))}
    </div>
    <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
      {Array.from({ length: 4 }, (_, index) => (
        <Skeleton key={index} className="h-72" />
      ))}
    </div>
  </div>
);

/** Finance dashboard: KPIs, what needs action, and the trends behind them */
export const FinanceOverview = () => {
  const translate = useTranslate();
  const { metrics, alerts, isEmpty, isPending, error, isTruncated } =
    useFinanceData();

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {translate("crm.finance.overview.error")}
        </AlertDescription>
      </Alert>
    );
  }
  if (isPending || !metrics || !alerts) return <OverviewSkeleton />;
  if (isEmpty) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <Landmark className="size-10 text-muted-foreground" />
        <p className="max-w-md text-sm text-muted-foreground">
          {translate("crm.finance.overview.empty")}
        </p>
        <Button asChild>
          <Link to="/finance/contracts">
            {translate("crm.finance.contracts.create")}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {isTruncated && (
        <Alert>
          <AlertDescription>
            {translate("crm.finance.overview.truncated")}
          </AlertDescription>
        </Alert>
      )}
      <KpiCards kpis={metrics.kpis} />
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <MrrChart monthly={metrics.monthly} />
        </div>
        <FinanceAlertsPanel alerts={alerts} />
      </div>
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <BilledReceivedChart monthly={metrics.monthly} />
        <ResultChart monthly={metrics.monthly} />
        <RevenueMixChart monthly={metrics.monthly} />
        <ForecastChart forecast={metrics.forecast} />
        <AgingChart aging={metrics.aging} />
        <ExpensesByCategoryChart categories={metrics.expensesByCategory} />
      </div>
      <ConcentrationChart concentration={metrics.concentration} />
    </div>
  );
};
